// Client-side READOUT of the seat's compute-credit balance. This is
// display-only — the authoritative balance and every debit are enforced
// server-side in backend/src/services/computeCredit.js. NEVER let this
// context's value gate whether a backtest run is allowed to submit; the
// server will reject an insufficient-credit run with 402 regardless of
// what this shows, and that's the source of truth (see
// docs/ARCHITECTURE.md §5).

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { backtestApi } from '../lib/apiClient.js';
import { useAuth } from './AuthContext.jsx';

const ComputeCreditContext = createContext(null);

export function ComputeCreditProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [wallet, setWallet] = useState(null); // { tier, balanceUsd }
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await backtestApi.creditBalance();
      setWallet(data);
    } catch {
      // Not fatal for the UI — the meter just shows "—" until available.
      setWallet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for auth to be fully resolved (token minted) before fetching the
    // balance — avoids a 401 when the very first request races auth init.
    if (authLoading) return;
    if (!user) {
      setWallet(null);
      setLoading(false);
      return;
    }
    refresh();
    // Re-fetch whenever auth state changes (mock apiClient ignores `user`
    // today; live wiring will need a real token, which refresh() already
    // fetches via apiClient's `auth: true` path).
  }, [refresh, user, authLoading]);

  return (
    <ComputeCreditContext.Provider value={{ wallet, loading, refresh }}>
      {children}
    </ComputeCreditContext.Provider>
  );
}

export function useComputeCredit() {
  const ctx = useContext(ComputeCreditContext);
  if (!ctx) throw new Error('useComputeCredit must be used within ComputeCreditProvider');
  return ctx;
}
